import React, { useMemo, useState } from "react";
import { useAppContext } from "../../../contexts/AppContext";
import FlightProgress from "../layout/FlightProgress";
import StickyBookingSummary from "../details/StickyBookingSummary";
import { getMockReviewViewModel } from "./reviewMockData";
import { resolveReviewAirlineIdentity } from "./resolveReviewAirlineIdentity";
import "./ReviewScreen.css";

function SectionCard({ title, editLabel, onEdit, compact = false, children }) {
  return (
    <section
      className={
        compact ? "fe2-rv-section fe2-rv-section--compact" : "fe2-rv-section"
      }
    >
      <header className="fe2-rv-section__head">
        <h2 className="fe2-rv-section__title">{title}</h2>
        {editLabel && onEdit ? (
          <button type="button" className="fe2-rv-edit" onClick={onEdit}>
            {editLabel}
          </button>
        ) : null}
      </header>
      <div className="fe2-rv-section__body">{children}</div>
    </section>
  );
}

/**
 * Review Design Preview — verification only, no data entry / pricing.
 */
export default function ReviewScreen({
  offer,
  search,
  review: reviewProp = null,
  onContinue,
  onBack,
  onEditSection,
}) {
  const { airlines } = useAppContext();
  const review = useMemo(
    () => reviewProp || getMockReviewViewModel(offer),
    [reviewProp, offer]
  );
  const airlineIdentity = useMemo(
    () =>
      resolveReviewAirlineIdentity({
        airlines,
        code: offer?.airline?.code,
        fallbackName: review.flight?.airlineLabel || offer?.airline?.name,
      }),
    [airlines, offer?.airline?.code, offer?.airline?.name, review.flight?.airlineLabel]
  );

  const [checks, setChecks] = useState(() =>
    Object.fromEntries((review.confirmation?.items || []).map((i) => [i.id, false]))
  );
  const [showFullTerms, setShowFullTerms] = useState(false);

  const allChecked =
    !review.cta?.requireConfirmation ||
    (review.confirmation?.items || []).every((i) => checks[i.id]);

  const derivedOffer = useMemo(
    () => ({
      ...offer,
      bookingSummary: review.bookingSummary,
    }),
    [offer, review.bookingSummary]
  );

  const handleEdit = (target) => {
    if (typeof onEditSection === "function") onEditSection(target);
  };

  return (
    <div className="fe2-rv-page">
      <header className="fe2-rv-head">
        <div className="fe2-rv-head__text">
          <h1 className="fe2-rv-head__title">{review.title}</h1>
          <p className="fe2-rv-head__subtitle">{review.subtitle}</p>
        </div>
        <button type="button" className="fe2-rv-head__back" onClick={onBack}>
          ← חזרה לתוספות
        </button>
      </header>

      <FlightProgress activeStep="review" />

      <div className="fe2-rv-page__grid">
        <div className="fe2-rv-page__main">
          <SectionCard
            title={review.flight.title}
            editLabel={review.flight.editLabel}
            onEdit={() => handleEdit(review.flight.editTarget)}
            compact
          >
            <p className="fe2-rv-flight__airline">
              {airlineIdentity.logoSrc ? (
                <img
                  src={airlineIdentity.logoSrc}
                  alt=""
                  className="fe2-rv-flight__logo"
                  decoding="async"
                />
              ) : null}
              <span className="fe2-ltr" data-fe2-airline-name="">
                {airlineIdentity.name}
              </span>
              {review.flight.fareFamily ? (
                <>
                  <span aria-hidden> · </span>
                  <span data-fe2-fare-family="">{review.flight.fareFamily}</span>
                </>
              ) : null}
            </p>
            <div className="fe2-rv-flight__legs">
              {(review.flight.legs || []).map((leg) => (
                <div key={leg.id} className="fe2-rv-flight__leg">
                  <span className="fe2-rv-flight__dir">{leg.directionLabel}</span>
                  <strong className="fe2-rv-flight__times fe2-ltr">
                    {leg.timeRouteLabel}
                  </strong>
                  <span className="fe2-rv-flight__meta fe2-ltr">{leg.metaLabel}</span>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard
            title={review.passengers.title}
            editLabel={review.passengers.editLabel}
            onEdit={() => handleEdit(review.passengers.editTarget)}
            compact
          >
            <ul className="fe2-rv-pax-list">
              {(review.passengers.items || []).map((pax) => (
                <li key={pax.id} className="fe2-rv-pax">
                  <div className="fe2-rv-pax__main">
                    <strong className="fe2-rv-pax__name fe2-ltr">{pax.name}</strong>
                    <span className="fe2-rv-pax__meta">{pax.metaLabel}</span>
                    {pax.passportSummary ? (
                      <span className="fe2-rv-pax__passport fe2-ltr">
                        {pax.passportSummary}
                      </span>
                    ) : null}
                  </div>
                  <span
                    className={`fe2-rv-pax__status fe2-rv-pax__status--${pax.statusTone || "complete"}`}
                  >
                    {pax.statusLabel}
                  </span>
                </li>
              ))}
            </ul>
          </SectionCard>

          <SectionCard
            title={review.baggage.title}
            editLabel={review.baggage.editLabel}
            onEdit={() => handleEdit(review.baggage.editTarget)}
          >
            <div className="fe2-rv-bag">
              {(review.baggage.passengers || []).map((pax) => (
                <div key={pax.id} className="fe2-rv-bag__pax">
                  <h3 className="fe2-rv-bag__name fe2-ltr">{pax.name}</h3>
                  {(pax.segments || []).map((seg) => (
                    <div key={seg.id} className="fe2-rv-bag__seg">
                      {seg.routeLabel ? (
                        <p className="fe2-rv-bag__route">
                          <span className="fe2-ltr">{seg.routeLabel}</span>
                        </p>
                      ) : null}
                      <div
                        className={`fe2-rv-bag__row fe2-rv-bag__row--${seg.tone || "default"}`}
                      >
                        <span className="fe2-rv-bag__item">{seg.itemLabel}</span>
                        {seg.valueLabel ? (
                          <span
                            className={
                              seg.tone === "paid"
                                ? "fe2-rv-bag__value fe2-ltr"
                                : "fe2-rv-bag__value"
                            }
                          >
                            {seg.valueLabel}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  ))}
                  {pax.subtotalLabel ? (
                    <div className="fe2-rv-bag__pax-subtotal">
                      <span>{pax.subtotalLabel}</span>
                      <strong className="fe2-ltr">{pax.subtotalAmountLabel}</strong>
                    </div>
                  ) : null}
                </div>
              ))}
              {review.baggage.paidTotalLabel ? (
                <div className="fe2-rv-bag__total">
                  <span>{review.baggage.paidTotalLabel}</span>
                  <strong className="fe2-ltr">
                    {review.baggage.paidTotalAmountLabel}
                  </strong>
                </div>
              ) : null}
            </div>
          </SectionCard>

          <SectionCard
            title={review.contact.title}
            editLabel={review.contact.editLabel}
            onEdit={() => handleEdit(review.contact.editTarget)}
          >
            <p className="fe2-rv-contact__name fe2-ltr">{review.contact.name}</p>
            <p className="fe2-rv-contact__line fe2-ltr">{review.contact.phone}</p>
            <p className="fe2-rv-contact__line fe2-ltr">{review.contact.email}</p>
          </SectionCard>

          <SectionCard title={review.fareConditions.title}>
            <dl className="fe2-rv-fare">
              {(review.fareConditions.rows || []).map((row) => (
                <div key={row.label} className="fe2-rv-fare__row">
                  <dt>{row.label}</dt>
                  <dd>{row.value}</dd>
                </div>
              ))}
            </dl>
            <button
              type="button"
              className="fe2-rv-fare__full"
              onClick={() => setShowFullTerms((v) => !v)}
            >
              {review.fareConditions.fullTermsLabel}
            </button>
            {showFullTerms ? (
              <p className="fe2-rv-fare__terms">{review.fareConditions.fullTerms}</p>
            ) : null}
          </SectionCard>

          <section className="fe2-rv-confirm">
            <h2 className="fe2-rv-confirm__title">{review.confirmation.title}</h2>
            <p className="fe2-rv-confirm__note">{review.confirmation.note}</p>
            <ul className="fe2-rv-confirm__list">
              {(review.confirmation.items || []).map((item) => (
                <li key={item.id}>
                  <label className="fe2-rv-confirm__item">
                    <input
                      type="checkbox"
                      checked={Boolean(checks[item.id])}
                      onChange={(e) =>
                        setChecks((prev) => ({
                          ...prev,
                          [item.id]: e.target.checked,
                        }))
                      }
                    />
                    <span>{item.label}</span>
                  </label>
                </li>
              ))}
            </ul>
          </section>
        </div>

        <div className="fe2-rv-page__side">
          <StickyBookingSummary
            offer={derivedOffer}
            search={search}
            onContinue={onContinue}
            onBack={onBack}
            continueLabel={review.cta?.continueLabel || "המשך לתשלום"}
            backLabel={review.cta?.backLabel || "חזרה לתוספות"}
            continueDisabled={!allChecked}
          />
        </div>
      </div>
    </div>
  );
}
